import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { request } from '@mairistem/tools';

import { baseUrlApi } from '@/routes';
import { Section } from '@/components/Common';
import PacsListeContainer from '@/components/Pacs/Liste/PacsListeContainer';
import AssistantContext from '@/contexts/AssistantContext';
import {
  mapConventionToPacs,
  mapRegistre,
  computeNextPacsNumber,
} from '@/utils';
import { useRegistreStore } from '@/zustand';

// Champs de saisie propres à la dissolution (motif + dates)
// Dans le cas de la dissolution, il faut les remettre à null
// pour éviter de les conserver dans la convention
const DISSOLUTION_INPUT_FIELDS = [
  'identifiantTypeDissolution',
  'dateDissolution',
  'dateMariage',
  'partenaire1DateDeces',
  'partenaire2DateDeces',
  'dateDeclarationUnitDi',
  'dateSignification',
  'dateDeclarationJointDi',
];

const StepSelectionPACS = () => {
  const { selectedDeclarationType, setSelectedPacs, onChange, onLoad } =
    React.useContext(AssistantContext);
  const registre = useRegistreStore((state) => state.registre);

  let typePacs = 'En-cours';
  let title = 'SÉLECTION DU PACS';

  switch (selectedDeclarationType) {
    case 'projet':
    case 'initiale':
      typePacs = 'Projet';
      title = 'SÉLECTION DU PROJET DE PACS';
      break;
  }
  const handleSelect = React.useCallback(
    (item: any) => {
      // Sélection d'un PACS existant (quel que soit le mode) : on récupère la
      // convention complète (avec partenaires/parents imbriqués) + le pacs pour récupérer
      // les compléments INSEE via convention/{id} et pacs/{id}
      if (!_.isNil(item?.pacs?.identifiant)) {
        Promise.all([
          request.get(`${baseUrlApi}/conventions/${item?.identifiant}`),
          request
            .get(`${baseUrlApi}/pacs/${item.pacs.identifiant}`)
            .catch(() => null),
        ]).then(([response, pacs]: [any, any]) => {
          const mapped = mapConventionToPacs(response, pacs?.complementInsee);

          if (selectedDeclarationType === 'initiale') {
            // Conversion projet → initiale : on repart du dernier registre
            // "initiale" déjà récupéré au clic sur le type de déclaration
            mapped.registre = mapRegistre(registre);
            mapped.numero = computeNextPacsNumber(registre as any);
          } else {
            mapped.numero =
              response?.pacs?.numero ?? item?.pacs?.numero ?? null;
          }

          if (selectedDeclarationType === 'dissolution') {
            DISSOLUTION_INPUT_FIELDS.forEach((field) => {
              mapped.conventionDeclaration[field] = null;
            });
          }

          if (selectedDeclarationType === 'modificative') {
            // On set dateEnregistrement à la date du jour lors d'une déclaration modificative
            mapped.conventionDeclaration.dateEnregistrement =
              dayjs().format('YYYY-MM-DD');
          }

          onLoad(mapped);
          // change selon si on récupère un PACS existant ou si on repart
          // d'une base vide
          setSelectedPacs(response.identifiantPacs ?? item?.pacs?.identifiant);
        });
        return;
      }

      // -1 signifie création : pas de base de PACS sélectionné
      setSelectedPacs(-1);

      onChange?.(null, {
        name: 'identifiantEntite',
        value: null,
      });

      onChange?.(null, {
        name: 'identifiant',
        value: null,
      });
    },
    [onChange, onLoad, registre, selectedDeclarationType, setSelectedPacs],
  );

  return (
    <Section
      data-testid="assistant-selection-pacs"
      title={title}
      style={{ minHeight: '100%' }}
    >
      <div />
      <PacsListeContainer
        data-testid="pacs-liste-assistant"
        stats
        typePacs={typePacs}
        onSelect={handleSelect}
        assistant
        projet={typePacs === 'Projet'}
      />
    </Section>
  );
};

export default StepSelectionPACS;
